import { getUrl } from "@insite/client-framework/ServerSideRendering";
import {
    MGBW_UPDATE_CART_LINE_ID,
    MGBW_UPDATE_QTY,
    MGBW_UPDATE_WISHLIST_ID,
    MGBW_UPDATE_WISHLIST_LINE_ID,
} from "@tSurya/Common/MgbwPropertyKeys";

export const PARAM_PREFIX = "oid-";

function getSearchString(): string {
    if (typeof window === "undefined") {
        var serverUrl = getUrl();
        return serverUrl?.search || "";
    }
    return window.location.search;
}

/**
 * Reads configurator option selections from the current URL query parameters.
 * Only includes "oid-"-prefixed params whose keys match known option IDs.
 * During SSR, reads from the server-side URL object.
 */
export function getSelectionsFromUrl(knownOptionIds: Set<string>): Record<string, string> {
    var search = getSearchString();
    if (!search) {
        return {};
    }
    var params = new URLSearchParams(search);
    var selections: Record<string, string> = {};
    knownOptionIds.forEach(optionId => {
        var value = params.get(PARAM_PREFIX + optionId);
        if (value) {
            selections[optionId] = value;
        }
    });
    return selections;
}

/**
 * Reads all "oid-"-prefixed query parameters from the current URL that could
 * be configurator selections, without filtering by known option IDs.
 * Strips the prefix before returning. Used to forward raw selections to the
 * backend product API so MgbwPopulateConfiguratorData can use them during
 * the initial product load.
 */
export function getRawSelectionsFromUrl(): Record<string, string> {
    var search = getSearchString();
    if (!search) {
        return {};
    }
    var params = new URLSearchParams(search);
    var selections: Record<string, string> = {};
    params.forEach((value, key) => {
        if (!value || !key.startsWith(PARAM_PREFIX)) {
            return;
        }
        var strippedKey = key.substring(PARAM_PREFIX.length);
        selections[strippedKey] = value;
    });
    return selections;
}

/**
 * Updates the URL query parameters to reflect the current configurator
 * selections. Uses replaceState to avoid polluting browser history.
 * Preserves any existing non-configurator query parameters.
 * All configurator params are written with the "oid-" prefix.
 * No-op during SSR.
 */
export function updateUrlWithSelections(
    selections: Record<string, string>,
    knownOptionIds: Set<string>,
): void {
    if (typeof window === "undefined") {
        return;
    }
    var params = new URLSearchParams(window.location.search);

    // Remove all existing configurator params
    knownOptionIds.forEach(optionId => {
        params.delete(PARAM_PREFIX + optionId);
    });

    // Add current selections with prefix
    Object.entries(selections).forEach(([optionId, valueId]) => {
        if (valueId) {
            params.set(PARAM_PREFIX + optionId, valueId);
        }
    });

    var search = params.toString();
    var newUrl = window.location.pathname + (search ? `?${search}` : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
}

/**
 * Reads the updateCartLineId query parameter from the current URL.
 * Returns the cart line ID string if present, or null if not found.
 * SSR-safe: reads from the server-side URL object during server-side rendering.
 */
export function getUpdateCartLineIdFromUrl(): string | null {
    var search = getSearchString();
    if (!search) {
        return null;
    }
    var params = new URLSearchParams(search);
    return params.get(MGBW_UPDATE_CART_LINE_ID) || null;
}

/**
 * Reads the updateWishListLineId and updateWishListId query parameters from the
 * current URL. Returns the wish list context if both are present, or null otherwise.
 * SSR-safe: reads from the server-side URL object during server-side rendering.
 */
export function getUpdateWishListContextFromUrl(): { wishListId: string; wishListLineId: string } | null {
    var search = getSearchString();
    if (!search) {
        return null;
    }
    var params = new URLSearchParams(search);
    var wishListLineId = params.get(MGBW_UPDATE_WISHLIST_LINE_ID);
    var wishListId = params.get(MGBW_UPDATE_WISHLIST_ID);
    if (!wishListLineId || !wishListId) {
        return null;
    }
    return { wishListId, wishListLineId };
}

/**
 * Reads the qty query parameter from the current URL, used to pre-select the
 * quantity on the PDP when reconfiguring an existing cart or project line.
 * Returns the quantity as a positive number, or null if not present or invalid.
 * SSR-safe: reads from the server-side URL object during server-side rendering.
 */
export function getQtyFromUrl(): number | null {
    var search = getSearchString();
    if (!search) {
        return null;
    }
    var params = new URLSearchParams(search);
    var raw = params.get(MGBW_UPDATE_QTY);
    if (!raw) {
        return null;
    }
    var qty = parseFloat(raw);
    return Number.isNaN(qty) || qty <= 0 ? null : qty;
}

/**
 * Appends configurator selections as query parameters to a given URL.
 */
export const appendSelectionsToUrl = (baseUrl: string, selections?: Record<string, string>) => {
    if (!selections || Object.keys(selections).length === 0) {
        return baseUrl;
    }

    const hasQuery = baseUrl.indexOf("?") >= 0;
    const separator = hasQuery ? "&" : "?";
    const query = Object.entries(selections)
        .filter(([optionId, valueId]) => !!optionId && !!valueId)
        .map(
            ([optionId, valueId]) =>
                `${encodeURIComponent(`${PARAM_PREFIX}${optionId}`)}=${encodeURIComponent(valueId)}`,
        )
        .join("&");

    return query ? `${baseUrl}${separator}${query}` : baseUrl;
};